"use client";

import { cn } from "@/lib/utils";
import { Slot } from "radix-ui";
import { tv, type VariantProps } from "tailwind-variants";

const buttonVariants = tv({
  base: "cursor-pointer border border-transparent inline-flex items-center justify-center whitespace-nowrap transition-colors duration-150 ease-linear hover:cursor-pointer rounded-none disabled:pointer-events-none disabled:opacity-30 shrink-0 outline-none group/button select-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring [&_svg:not([class*='size-'])]:size-4.5 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  variants: {
    variant: {
      default:
        "bg-black text-white hover:bg-black/80 all-small-caps tracking-wider uppercase no-underline",
      outline:
        "border-black hover:bg-black hover:text-white all-small-caps tracking-wider uppercase",
      secondary:
        "bg-primary-500 text-black hover:bg-primary-500/80 all-small-caps tracking-wider uppercase",
      ghost: "bg-transparent all-small-caps tracking-wider uppercase",
      destructive:
        "all-small-caps tracking-wider uppercase bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30",
      link: "underline-offset-2 hover:underline hover:text-muted-foreground",
      search: "bg-transparent justify-start",
      cta: "all-small-caps tracking-wider uppercase underline underline-offset-4 px-0",
      underline:
        "underline underline-offset-2 text-muted-foreground hover:text-foreground",
    },
    size: {
      auto: "h-auto w-auto p-1",
      default: "h-11 gap-2 px-5",
      xs: "h-8 gap-1 px-3 text-sm [&_svg:not([class*='size-'])]:size-3",
      sm: "h-9 gap-1.5 px-4 text-sm [&_svg:not([class*='size-'])]:size-4",
      lg: "h-12 gap-2 px-6 [&_svg:not([class*='size-'])]:size-5",
      icon: "size-11",
      "icon-xs": "size-8 [&_svg:not([class*='size-'])]:size-4",
      "icon-sm": "size-9 [&_svg:not([class*='size-'])]:size-5",
      "icon-lg": "size-12 [&_svg:not([class*='size-'])]:size-6",
      link: "p-2 gap-1.5 [&_svg:not([class*='size-'])]:size-4",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Component = asChild ? Slot.Root : "button";

  return (
    <Component
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(
        buttonVariants({
          variant,
          size,
          className,
        }),
      )}
      {...props}
    />
  );
}

export { Button, buttonVariants };
